import { FormSectionHandler } from '../entity.handler';
import { ClientResponse, ClientUpdateRequest, ThemeInfo } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, ComboField, MemoField, postImageForm, dataURItoFile, IdNameComboValueHandlers } from '../utils';
import * as Bluebird from 'bluebird';
import { HttpClient } from '@angular/common/http';

interface EditorValues {
    logo?: { cropped: string, preview: string };
    logoWeb?: { cropped: string, preview: string };
    domains?: string;
    theme?: ThemeInfo;
    language?: string;
    loginMessage?: string;
}

export class VendorCustomizeSectionHandler implements FormSectionHandler<ClientResponse, EditorValues> {

    type = 'form';
    icon = 'paint-roller';
    bulk = true;
    readonly = false;
    vendorId: string;
    webLogoTouched: boolean;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public httpClient: HttpClient
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.VENDOR.CUSTOMIZE.${key}`, values);
    }

    async applyUpdate(entity: ClientResponse, payload: ClientUpdateRequest): Promise<ClientResponse> {
        const updateLogo = async (name: string, size: string) => {
            if (payload[name]) {
                const logo = payload[name];
                delete payload[name];
                if (logo.cropped && logo.cropped.startsWith('data:')) {
                    await postImageForm(this.httpClient, `${this.admin.apiEndpoint}/accounts/vendors/${entity.id}/logo?size=${size}`, this.admin.apiAccessToken, dataURItoFile(logo.cropped));
                    await Bluebird.delay(500); // wait a while as the api rate limiting is gonna freak out if we do 3 updates in quick succession
                }
                if (logo.cropped === null) {
                    await this.admin.applyUpdate(api => api.accounts.deleteVendorLogo(entity.id, size));
                    await Bluebird.delay(500); // wait a while as the api rate limiting is gonna freak out if we do 3 updates in quick succession
                }
            }
        };
        await updateLogo('logo', 'large');
        await updateLogo('logoWeb', 'small');
        this.webLogoTouched = false;
        return this.admin.applyUpdate(api => api.accounts.updateVendorDetails(entity.id, payload));
    }

    async mapEntityToEditor(entity: ClientResponse): Promise<EditorValues> {
        this.vendorId = entity.id;
        const logo = `${this.admin.apiEndpoint}/accounts/vendors/${entity.id}/logo?size=large&_=${(new Date()).getTime()}`;
        const logoWeb = `${this.admin.apiEndpoint}/accounts/vendors/${entity.id}/logo?size=small&_=${(new Date()).getTime()}`;
        const result = {
            logo: {
                preview: logo,
                cropped: logo,
            },
            logoWeb: {
                preview: logoWeb,
                cropped: logoWeb,
            },
            domains: (entity.domains || []).join('\n'),
            theme: entity.theme,
            language: entity.language,
            loginMessage: entity.messages && entity.messages.login,
        };
        return result;
    }

    async mapEditorToEntity(entity: ClientUpdateRequest, values: EditorValues): Promise<any> {
        if (values.domains !== undefined) {
            entity.domains = values.domains.replace(/\\r/g, '\n').split('\n').filter(x => x);
        }
        if (values.theme) { entity.theme = values.theme; }
        if (values.language) { entity.language = values.language; }
        if (values.loginMessage !== undefined) { entity.messages = { login: values.loginMessage }; }
        if (values.logo) { entity['logo'] = values.logo; }
        if (values.logoWeb || values.logo) {
            const hasCompanyWebLogo = await this.admin.getCached('getCompanyLogo/small', api => api.accounts.getCompanyLogo(this.vendorId, 'small'), null, true).then(() => true).catch(() => null);
            const useLogoAsWebLogo = !hasCompanyWebLogo && values.logo && values.logo.cropped && values.logo.cropped.startsWith('data:');
            if (values.logoWeb && (hasCompanyWebLogo || this.webLogoTouched)) {
                entity['logoWeb'] = {
                    preview: values.logoWeb.preview,
                    cropped: values.logoWeb.preview,
                };
            } else if (useLogoAsWebLogo) {
                entity['logoWeb'] = {
                    preview: values.logo.preview,
                    cropped: values.logo.preview,
                };
            }
        }
        if (values.theme) { entity.theme = values.theme; }
        return Promise.resolve(entity);
    }

    getFields(entity: ClientResponse): Promise<FormBuilderField[]> {
        const themeId = entity ? entity.id : this.ownerId;
        return Promise.all([
            this.admin.getCached(`listThemes/${themeId}/true/0/100/name`, api => api.entities.listThemes(themeId, true, 0, 100, 'name')),
            this.admin.getLanguages(),
        ]).then(([themes, languages]) => {
            return [
                {
                    id: 'logo', title: this.translate('FIELDS.LOGO'), type: 'image', description: this.translate('FIELDS.LOGO_DESC'), onChange: async (_field, values) => {
                        const hasCompanyWebLogo = await this.admin.getCached('getCompanyLogo/small', api => api.accounts.getCompanyLogo(this.vendorId, 'small'), null, true).then(() => true).catch(() => null);
                        if (!hasCompanyWebLogo && !this.webLogoTouched) {
                            values['logoWeb'] = {
                                preview: values.logo.preview,
                                cropped: values.logo.preview,
                            };
                        }
                        return;
                    },
                },
                { id: 'logoWeb', title: this.translate('FIELDS.WEB_LOGO'), type: 'image', description: this.translate('FIELDS.WEB_LOGO_DESC'), options: { background: 'transparent' }, width: 120, onChange: async () => {
                    this.webLogoTouched = true;
                }},
                this.admin.user.owner.type !== 'vendor' ? MemoField('domains', this.translate('FIELDS.DOMAINS'), false, 1, 10000) : null,
                ComboField('language', this.translate('FIELDS.LANGUAGE'), false, languages.map(x => ({ key: x.id, value: x.name }))),
                ComboField('theme', this.translate('FIELDS.THEME'), false, [{ key: 'inherited', value: 'Inherited' }, ...themes.items.filter(x => x['state'] === 'active').map(x => ({ key: x.id, value: x.name }))], IdNameComboValueHandlers),
                { ...MemoField('loginMessage', this.translate('FIELDS.LOGIN'), false, 0, 100000), description: this.translate('FIELDS.LOGIN_DESC') },
            ].filter(x => x);
        }).then(fields => EntityUtils.prepareBulk(!entity, ['language', 'theme', 'loginMessage'], fields));
    }

}
